/**
 * 입력 정합성·입력 편의 (15차) — 순수 도우미 (DOM 없음, 단위 검사 대상)
 *  - 바이트 길이 (Oracle AL32UTF8 = utf8: 한글 3바이트, KO16MSWIN949 = euc-kr: 한글 2바이트)
 *  - 검증 결과 수준 (error · warning · info)
 *  - 체크 콤보 값 (구분자로 이은 코드 문자열 ↔ 목록)
 *  - 값 쓰기 전 이벤트 경로 (onBeforeChanges → onBeforeChange)
 *  - 채우기 (Ctrl+D 아래로 · Ctrl+R 오른쪽으로)
 */
/** 글자 하나(코드 포인트)의 바이트 수 */
export function charBytes(cp, enc = 'utf8') {
    if (enc === 'euc-kr')
        return cp < 0x80 ? 1 : 2;
    return cp < 0x80 ? 1 : cp < 0x800 ? 2 : cp < 0x10000 ? 3 : 4;
}
/** 문자열 바이트 길이 (null·undefined = 0). 서로게이트 쌍은 한 글자 (utf8 4바이트) */
export function byteLength(s, enc = 'utf8') {
    if (s == null)
        return 0;
    const t = String(s);
    let n = 0;
    if (enc === 'euc-kr') {
        for (let i = 0; i < t.length; i++) {
            const c = t.charCodeAt(i);
            if (c >= 0xd800 && c <= 0xdbff && i + 1 < t.length)
                i++;
            n += c < 0x80 ? 1 : 2;
        }
        return n;
    }
    for (let i = 0; i < t.length; i++) {
        const c = t.charCodeAt(i);
        if (c < 0x80)
            n += 1;
        else if (c < 0x800)
            n += 2;
        else if (c >= 0xd800 && c <= 0xdbff && i + 1 < t.length) {
            n += 4;
            i++;
        }
        else
            n += 3;
    }
    return n;
}
/** max 바이트 안에 드는 가장 긴 앞부분 (글자 중간에서 자르지 않음) */
export function clipBytes(s, max, enc = 'utf8') {
    let n = 0, i = 0;
    for (const ch of s) {
        const b = charBytes(ch.codePointAt(0), enc);
        if (n + b > max)
            break;
        n += b;
        i += ch.length;
    }
    return s.slice(0, i);
}
/**
 * 입력 중 바이트 한도 지키기: 이전 값(prev, 한도 안)에서 next 로 바뀌었을 때 새로 들어온 부분만 한도에 맞게 자른다
 * (가운데에 끼워 넣어도 뒤 글자는 그대로). prev 도 넘으면 앞부분만 남김. 한도 안이면 next 그대로
 */
export function fitBytes(prev, next, max, enc = 'utf8') {
    if (byteLength(next, enc) <= max)
        return next;
    if (byteLength(prev, enc) > max)
        return clipBytes(next, max, enc);
    let p = 0;
    const lim = Math.min(prev.length, next.length);
    while (p < lim && prev.charCodeAt(p) === next.charCodeAt(p))
        p++;
    let s = 0;
    while (s < lim - p && prev.charCodeAt(prev.length - 1 - s) === next.charCodeAt(next.length - 1 - s))
        s++;
    // 서로게이트 쌍 가운데에서 나누지 않게
    if (p > 0 && /[\ud800-\udbff]/.test(next[p - 1]))
        p--;
    const head = next.slice(0, p), tail = next.slice(next.length - s), ins = next.slice(p, next.length - s);
    const room = max - byteLength(head, enc) - byteLength(tail, enc);
    if (room < 0)
        return clipBytes(next, max, enc);
    return head + clipBytes(ins, room, enc) + tail;
}
/** 검증 결과 → { level, message } (통과면 null). 알 수 없는 level 은 error */
export function normValidation(r) {
    if (r == null || r === false || r === '')
        return null;
    if (typeof r === 'string')
        return { level: 'error', message: r };
    if (typeof r === 'object' && 'message' in r) {
        const m = r.message;
        if (m == null || m === '')
            return null;
        const lv = r.level;
        return { level: lv === 'warning' || lv === 'info' ? lv : 'error', message: String(m) };
    }
    return { level: 'error', message: String(r) };
}
/** 수준 순위 (error 3 > warning 2 > info 1) */
export const LEVEL_RANK = { error: 3, warning: 2, info: 1 };
// ── 체크 콤보 (여러 값 선택) ──
/** 저장 값 → 코드 목록 (구분자 sep, 앞뒤 공백 무시, 빈 칸 버림) */
export function splitMulti(v, sep = ',') {
    if (v == null || v === '')
        return [];
    if (Array.isArray(v))
        return v.map(x => String(x).trim()).filter(Boolean);
    return String(v).split(sep).map(x => x.trim()).filter(Boolean);
}
/** 코드 목록 → 저장 값: 목록(items) 순서로 정렬, 겹친 것 버림, 비면 null */
export function joinMulti(values, items, sep = ',') {
    const set = new Set(values);
    const out = items ? items.filter(i => set.has(i.value)).map(i => i.value) : [...set];
    if (items)
        for (const v of set)
            if (!items.some(i => i.value === v))
                out.push(v); // 목록에 없는 코드는 뒤에 (예전 값 보존)
    return out.length ? out.join(sep) : null;
}
/** 저장 값 → 화면 글자 (명칭을 dispSep 로 이음, 목록에 없는 코드는 코드 그대로) */
export function formatMulti(v, items, sep = ',', dispSep = ', ', show = 'label') {
    const vals = splitMulti(v, sep);
    if (!vals.length)
        return '';
    const m = new Map(items.map(i => [i.value, i]));
    return vals.map(x => { const it = m.get(x); return !it || show === 'value' ? x : show === 'both' ? `${it.value} ${it.label}` : it.label; }).join(dispSep);
}
/**
 * 붙여넣기·바꾸기·폼 글자 → 체크 콤보 값. 쉼표·세미콜론·구분자·표시 구분자로 나눈 조각마다 코드 또는 명칭(대소문자 무시)이어야 함.
 * 목록에 없는 조각이 있거나 maxSelect 를 넘으면 { error }
 */
export function parseMulti(text, items, o = {}) {
    const sep = o.sep ?? ',', ds = (o.dispSep ?? ', ').trim();
    const t = text.trim();
    if (!t)
        return { value: null };
    const esc = (x) => x.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = t.split(new RegExp([sep, ds, ',', ';'].filter(Boolean).map(esc).join('|'))).map(x => x.trim()).filter(Boolean);
    const out = [];
    for (const p of parts) {
        const q = p.toLowerCase();
        const it = items.find(i => i.value.toLowerCase() === q) ?? items.find(i => i.label.toLowerCase() === q);
        if (!it)
            return { error: '목록에 없는 값입니다', bad: p };
        if (!out.includes(it.value))
            out.push(it.value);
    }
    if (o.maxSelect != null && out.length > o.maxSelect)
        return { error: `최대 ${o.maxSelect}개까지 고를 수 있습니다` };
    return { value: joinMulti(out, items, sep) };
}
/**
 * 값 쓰기 전 이벤트를 한 번에 거침 (그리드 쓰기 경로의 한 곳): onBeforeChanges(list) 가 false 면 전부 취소, 배열이면 그것으로 바꿈 →
 * 셀마다 onBeforeChange — false = 그 셀 취소, { value } = 값 바꿈. 남은 변경을 돌려준다 (순서 유지)
 */
export function routeBeforeChange(changes, source, getOld, one, many) {
    if (!changes.length || (!one && !many))
        return { kept: changes, cancelled: 0 };
    let list = changes.map(c => ({ ...c, oldValue: getOld(c.row, c.col), source }));
    if (many && changes.length > 1) {
        const r = many(list.map(x => ({ row: x.row, col: x.col, value: x.value, oldValue: x.oldValue, source })), source);
        if (r === false)
            return { kept: [], cancelled: changes.length };
        if (Array.isArray(r)) {
            const byKey = new Map(list.map(x => [x.row + ':' + x.col, x]));
            list = r.filter(x => x && byKey.has(x.row + ':' + x.col)).map(x => ({ ...byKey.get(x.row + ':' + x.col), value: x.value }));
        }
    }
    const kept = [];
    for (const x of list) {
        let v = x.value;
        if (one) {
            const r = one({ row: x.row, col: x.col, value: v, oldValue: x.oldValue, source });
            if (r === false)
                continue;
            if (r && typeof r === 'object' && 'value' in r)
                v = r.value;
        }
        const { oldValue: _o, source: _s, ...rest } = x;
        kept.push({ ...rest, value: v });
    }
    return { kept, cancelled: changes.length - kept.length };
}
// ── 채우기 (Ctrl+D·Ctrl+R) ──
/**
 * 아래로(down: 맨 윗행 값 → 아래 행들) · 오른쪽(right: 맨 왼쪽 값 → 오른쪽 칸들) 복사 계획.
 * rows·cols 는 선택 범위의 물리 행·컬럼 (그룹 머리행 등은 빼고 줌). 한 줄만 고르면 바로 위 행(above)·왼쪽 칸(left) 값을 복사 (엑셀)
 */
export function fillCopyPlan(rows, cols, dir, get, ctx = {}) {
    const out = [];
    if (!rows.length || !cols.length)
        return out;
    if (dir === 'down') {
        const single = rows.length === 1;
        const src = single ? ctx.above : rows[0];
        if (src == null || src < 0)
            return out;
        for (const c of cols) {
            const v = get(src, c);
            for (const r of single ? rows : rows.slice(1))
                out.push({ row: r, col: c, value: v });
        }
    }
    else {
        const single = cols.length === 1;
        const src = single ? ctx.left : cols[0];
        if (src == null)
            return out;
        for (const r of rows) {
            const v = get(r, src);
            for (const c of single ? cols : cols.slice(1))
                out.push({ row: r, col: c, value: v });
        }
    }
    return out;
}
